import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Trash2, Lock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "goox_admin_token";

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId} className="card-soft p-5">
    <p className="eyebrow text-mist">{label}</p>
    <p className="font-display mt-2 text-3xl font-extrabold text-ink">{value}</p>
  </div>
);

const Login = ({ onToken }) => {
  const { t } = useLang();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      onToken(data.token);
    } catch (ex) {
      setErr(ex.response?.status === 429 ? t.admin.locked : t.admin.invalid);
    } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md pt-32">
      <form data-testid="admin-login-form" onSubmit={submit} className="card-soft p-8">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 text-violet-700"><Lock size={18} /></span>
        <h1 className="font-display mt-5 text-2xl font-extrabold text-ink">{t.admin.title}</h1>
        <p className="mt-1 text-sm text-mist">{t.admin.sub}</p>
        <label className="mt-6 block text-sm font-semibold text-ink" htmlFor="admin-pw">{t.admin.password}</label>
        <input id="admin-pw" data-testid="admin-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-2 w-full rounded-xl border border-line bg-canvas px-4 py-3 text-sm outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100" />
        {err && <p data-testid="admin-login-error" className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
        <button data-testid="admin-login-button" disabled={busy || !pw} className="pill btn-primary mt-6 w-full py-3 text-sm font-semibold disabled:opacity-60">{busy ? t.admin.loggingIn : t.admin.login}</button>
      </form>
    </div>
  );
};

export default function Admin() {
  const { t } = useLang();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");

  const logout = useCallback(() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); setData(null); }, []);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/admin/waitlist`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch (ex) {
      if (ex.response?.status === 401) logout();
    }
  }, [token, logout]);

  useEffect(() => { load(); }, [load]);

  const onToken = (tk) => { sessionStorage.setItem(TOKEN_KEY, tk); setToken(tk); };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/waitlist/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success(t.admin.deleted);
    load();
  };

  const exportCsv = () => {
    const rows = [["email", "role", "lang", "consent", "created_at"], ...(data?.entries || []).map((e) => [e.email, e.role, e.lang, e.consent, e.created_at])];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "goox-waitlist.csv"; a.click();
  };

  if (!token) return <main data-testid="admin-page" className="mx-auto max-w-7xl px-4 pb-28 sm:px-6 lg:px-8"><Login onToken={onToken} /></main>;

  const entries = (data?.entries || []).filter((e) => (role === "all" || e.role === role) && e.email.includes(q.toLowerCase()));

  return (
    <main data-testid="admin-page" className="mx-auto max-w-7xl px-4 pb-28 pt-28 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-violet-600">{t.admin.sub}</p>
          <h1 data-testid="admin-title" className="font-display mt-2 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">{t.admin.title}</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-refresh-button" onClick={load} className="pill btn-ghost inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold"><RefreshCw size={14} /> {t.admin.refresh}</button>
          <button data-testid="admin-export-button" onClick={exportCsv} className="pill btn-ghost inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold"><Download size={14} /> {t.admin.export}</button>
          <button data-testid="admin-logout-button" onClick={logout} className="pill btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold"><LogOut size={14} /> {t.admin.logout}</button>
        </div>
      </div>

      <div className="mt-8 grid gap-3 sm:grid-cols-4">
        <Stat label={t.admin.total} value={data?.total ?? "—"} testId="admin-stat-total" />
        <Stat label={t.admin.fans} value={data?.by_role?.fan ?? "—"} testId="admin-stat-fans" />
        <Stat label={t.admin.creators} value={data?.by_role?.creator ?? "—"} testId="admin-stat-creators" />
        <Stat label={t.admin.agencies} value={data?.by_role?.agency ?? "—"} testId="admin-stat-agencies" />
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.admin.search} className="w-full rounded-full border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-violet-500 sm:max-w-sm" />
        <select data-testid="admin-role-filter" value={role} onChange={(e) => setRole(e.target.value)} className="rounded-full border border-line bg-white px-4 py-2.5 text-sm outline-none">
          <option value="all">{t.admin.all}</option>
          {["fan", "creator", "agency"].map((r) => <option key={r} value={r}>{t.admin.roles[r]}</option>)}
        </select>
      </div>

      <div data-testid="admin-table-container" className="card-soft mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-lilac text-left text-xs uppercase tracking-wider text-mist">
            <tr>
              <th className="px-5 py-3">{t.admin.cols.email}</th>
              <th className="px-5 py-3">{t.admin.cols.role}</th>
              <th className="px-5 py-3">{t.admin.cols.lang}</th>
              <th className="px-5 py-3">{t.admin.cols.consent}</th>
              <th className="px-5 py-3">{t.admin.cols.date}</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {entries.length === 0 ? (
              <tr><td colSpan={6} data-testid="admin-empty" className="px-5 py-10 text-center text-mist">{t.admin.empty}</td></tr>
            ) : entries.map((e) => (
              <tr key={e.id} data-testid={`admin-row-${e.id}`} className="hover:bg-canvas">
                <td className="px-5 py-3 font-medium text-ink">{e.email}</td>
                <td className="px-5 py-3"><span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-800">{t.admin.roles[e.role]}</span></td>
                <td className="px-5 py-3 uppercase text-ink-2">{e.lang}</td>
                <td className="px-5 py-3 text-ink-2">{e.consent ? "✓" : "—"}</td>
                <td className="px-5 py-3 text-ink-2">{new Date(e.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 text-right"><button data-testid={`admin-delete-${e.id}`} onClick={() => remove(e.id)} className="rounded-full p-2 text-mist hover:bg-rose-50 hover:text-rose-600" aria-label={t.admin.delete}><Trash2 size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
